/**
 * Pure zone planning: backend regions + placed cameras → drawable zone plan.
 *
 * Pipeline:
 *  1. Pair PDF name labels ↔ cameras by exact name (ambiguous names skipped)
 *     and fit the PDF→editor similarity from the pairs.
 *  2. Vote in PDF space: name labels inside each zone polygon, counted by the
 *     zone-number segment (`P1_B2_2_6` → `2`). Majority names the zone
 *     `{segment}구역`; dissenting labels are reported, never auto-fixed.
 *  3. Report zone numbers whose cameras fall outside every recovered polygon
 *     (구역선 미검출 — the user draws those manually; NO fallback generation).
 *
 * JointJS-agnostic and side-effect free so unit tests can inject plain data.
 */

import { floorZoneName, nextFloorZoneName } from '../../../map-editor-shared/lib/zoneName';
import {
  applyTransform,
  fitSimilarityFromPairs,
  MIN_SIMILARITY_PAIRS,
  type Pt,
  type SimilarityPair,
} from './transform';
import type {
  PlannedZone,
  ZoneCamera,
  ZonePlan,
  ZoneRegionsResult,
} from '../model/types';

/** Camera/label naming scheme the zone vote relies on (e.g. `P1_B2_2_6`). */
export const CCTV_NAME_RE = /^P\d+_B\d+_\d+_\d+$/;
/** `P1_B2_2_6`.split('_')[2] — the zone number segment. */
const ZONE_SEGMENT_INDEX = 2;
/** At most this many dissenting label names are listed per zone. */
const MIXED_VOTE_NAME_CAP = 5;

/**
 * Ray-casting point-in-polygon for an unclosed ring (last != first).
 * A point exactly on a vertex counts as inside.
 */
export function pointInPolygon(pt: Pt, ring: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (a.x === pt.x && a.y === pt.y) return true;
    const crosses =
      (a.y > pt.y) !== (b.y > pt.y) &&
      pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Build the zone creation plan.
 *
 * Throws a user-facing `Error` when fewer than {@link MIN_SIMILARITY_PAIRS}
 * (label name ↔ camera) pairs exist — names must be imported first.
 */
export function buildZonePlan(
  regions: ZoneRegionsResult,
  cameras: ZoneCamera[],
  existingZoneNames: string[],
  /** 지금 층 이름(B1) — 구역 이름이 B1_3 처럼 층_번호가 된다. 없으면 '3구역'. */
  floorName?: string
): ZonePlan {
  const camsByName = new Map<string, ZoneCamera[]>();
  for (const cam of cameras) {
    const list = camsByName.get(cam.name);
    if (list) {
      list.push(cam);
    } else {
      camsByName.set(cam.name, [cam]);
    }
  }

  // 라벨↔카메라 정확 이름 매칭. 같은 이름의 카메라가 여러 대면 모호 → 제외.
  const pairs: SimilarityPair[] = [];
  const pairedCams: ZoneCamera[] = [];
  const seenLabelNames = new Set<string>();
  const validLabels = regions.nameLabels.filter((l) =>
    CCTV_NAME_RE.test(l.name)
  );
  for (const label of validLabels) {
    if (seenLabelNames.has(label.name)) continue;
    seenLabelNames.add(label.name);
    const matched = camsByName.get(label.name);
    if (!matched || matched.length !== 1) continue;
    pairs.push({
      pdf: { x: label.x, y: label.y },
      editor: { x: matched[0].x, y: matched[0].y },
    });
    pairedCams.push(matched[0]);
  }
  if (pairs.length < MIN_SIMILARITY_PAIRS) {
    throw new Error(
      '이름이 매핑된 CCTV가 부족합니다. 먼저 🏷️ CCTV 이름을 적용하세요.'
    );
  }
  const transform = fitSimilarityFromPairs(pairs);

  const transformedRings: Pt[][] = regions.zones.map((zone) =>
    zone.points.map(([x, y]) => applyTransform(transform, { x, y }))
  );

  const existing = new Set(existingZoneNames);
  const zones: PlannedZone[] = [];
  const skippedExisting: string[] = [];
  // 이름 없는 구역의 순번 — 기존 zone 수에 이어서. 층을 알면 층_번호 중 안 쓴 번호.
  let sequence = existingZoneNames.length;
  const planned: string[] = [];

  regions.zones.forEach((zone, zoneIndex) => {
    const ringPdf: Pt[] = zone.points.map(([x, y]) => ({ x, y }));
    const votes = new Map<string, string[]>();
    for (const label of validLabels) {
      if (!pointInPolygon({ x: label.x, y: label.y }, ringPdf)) continue;
      const segment = label.name.split('_')[ZONE_SEGMENT_INDEX];
      const names = votes.get(segment);
      if (names) {
        names.push(label.name);
      } else {
        votes.set(segment, [label.name]);
      }
    }

    let name: string;
    let noVotes = false;
    let mixedVoteCount = 0;
    let mixedVoteNames: string[] = [];
    if (votes.size === 0) {
      noVotes = true;
      sequence += 1;
      name = floorName?.trim()
        ? nextFloorZoneName(floorName, [...existing, ...planned], sequence)
        : `Zone-${sequence}`;
    } else {
      let majoritySegment = '';
      let majorityCount = 0;
      for (const [segment, names] of votes) {
        if (names.length > majorityCount) {
          majoritySegment = segment;
          majorityCount = names.length;
        }
      }
      name = floorZoneName(floorName, majoritySegment);
      for (const [segment, names] of votes) {
        if (segment === majoritySegment) continue;
        mixedVoteCount += names.length;
        mixedVoteNames.push(...names);
      }
      mixedVoteNames = mixedVoteNames.slice(0, MIXED_VOTE_NAME_CAP);
    }

    if (existing.has(name)) {
      skippedExisting.push(name);
      return;
    }
    planned.push(name);
    zones.push({
      name,
      points: transformedRings[zoneIndex].map((p) => ({ x: p.x, y: p.y })),
      mixedVoteCount,
      mixedVoteNames,
      noVotes,
    });
  });

  // 폴리곤이 검출되지 않은 구역 번호: 그 번호의 paired 카메라 과반이
  // 변환된 모든 zone 폴리곤 밖에 있으면 수동 작도가 필요한 구역으로 보고.
  const camsBySegment = new Map<string, ZoneCamera[]>();
  for (const cam of pairedCams) {
    const segment = cam.name.split('_')[ZONE_SEGMENT_INDEX];
    const list = camsBySegment.get(segment);
    if (list) {
      list.push(cam);
    } else {
      camsBySegment.set(segment, [cam]);
    }
  }
  const uncoveredSegments: string[] = [];
  for (const [segment, cams] of camsBySegment) {
    let outside = 0;
    for (const cam of cams) {
      const covered = transformedRings.some((ring) =>
        pointInPolygon({ x: cam.x, y: cam.y }, ring)
      );
      if (!covered) outside += 1;
    }
    if (outside * 2 > cams.length) uncoveredSegments.push(segment);
  }
  uncoveredSegments.sort((a, b) => Number(a) - Number(b));

  return {
    zones,
    skippedExisting,
    uncoveredSegments,
    transform: {
      medResidual: transform.medResidual,
      pairCount: transform.pairCount,
      inlierCount: transform.inlierCount,
    },
  };
}
